import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
} from '@mui/material';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import {
  PermissionGate,
  Permissions,
  usePermission,
} from '@billda/permissions-ui';
import { UserStatusBadge } from '../atoms/UserStatusBadge';
import { UserStatus } from '../../../api/userManagementApi';
import type { UserDetailDto } from '../../../api/userManagementApi';
import { getUserDisplayName } from '../../../utils/userDisplayUtils';
import { canManageUserOffboarding } from '../../../utils/roleChangePolicy';
import { routeBuilders } from '../../../../../routes';

interface UserOverviewCardProps {
  user: UserDetailDto;
  formatDate: (dateString: string) => string;
  isCurrentUser: boolean;
}

export function UserOverviewCard({
  user,
  formatDate,
  isCurrentUser,
}: UserOverviewCardProps) {
  const navigate = useNavigate();
  const canManageAdmins = usePermission(Permissions.Users.ManageAdmins);
  const canOffboard = canManageUserOffboarding({
    roles: user.roles,
    isCurrentUser,
    canManageAdmins,
  });
  const getPrimaryPhone = () => {
    if (!user.phoneNumbers || user.phoneNumbers.length === 0) return null;
    const primary = user.phoneNumbers.find(p => p.isPrimary);
    const phone = primary || user.phoneNumbers[0];
    return `${phone.phoneNumber}${primary ? ' (Primary)' : ''}`;
  };

  const primaryPhone = getPrimaryPhone();

  return (
    <Card variant="outlined" data-testid="user-overview-card">
      <CardContent>
        <Stack spacing={2}>
          {/* User Name and Status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant="h6" component="h2">
              {getUserDisplayName(user)}
            </Typography>
            {user.status && <UserStatusBadge status={user.status} />}
          </Box>

          {/* User Details */}
          <Stack spacing={1.5}>
            {/* Roles */}
            {user.roles && user.roles.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Roles
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {user.roles.map(role => role.roleName).join(', ')}
                </Typography>
              </Box>
            )}

            {/* Email */}
            {user.email && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {user.email}
                </Typography>
              </Box>
            )}

            {/* Phone */}
            {primaryPhone && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {primaryPhone}
                </Typography>
              </Box>
            )}

            {/* UTR */}
            {user.profile?.utrNumber && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  UTR
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {user.profile.utrNumber}
                </Typography>
              </Box>
            )}

            {/* Joined */}
            {user.joinedAt && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Joined
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(user.joinedAt)}
                </Typography>
              </Box>
            )}
          </Stack>
          <FeatureSwitchGate
            feature={Features.Offboarding.IsEnabled}
            fallback={null}
          >
            <PermissionGate
              permission={Permissions.Users.Manage}
              fallback={null}
            >
              {canOffboard &&
                (user.status === UserStatus.Active ||
                  user.status === UserStatus.Offboarding) && (
                  <Button
                    variant="outlined"
                    color={
                      user.status === UserStatus.Active ? 'error' : 'warning'
                    }
                    fullWidth
                    onClick={() =>
                      navigate(routeBuilders.users.offboarding(user.id))
                    }
                    data-testid="offboard-manage-button"
                  >
                    {user.status === UserStatus.Active
                      ? 'Offboard'
                      : 'Manage Offboarding'}
                  </Button>
                )}
            </PermissionGate>
          </FeatureSwitchGate>
        </Stack>
      </CardContent>
    </Card>
  );
}
